import React from 'react';

// Modern color palette
export const colors = {
    primary: '#1e40af',
    primaryLight: '#3b82f6',
    primaryDark: '#1e3a8a',
    secondary: '#7c3aed',
    success: '#10b981',
    danger: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6',

    // Neutrals
    gray50: '#f9fafb',
    gray100: '#f3f4f6',
    gray200: '#e5e7eb',
    gray300: '#d1d5db',
    gray400: '#9ca3af',
    gray500: '#6b7280',
    gray600: '#4b5563',
    gray700: '#374151',
    gray800: '#1f2937',
    gray900: '#111827',

    // Backgrounds
    bgPrimary: '#ffffff',
    bgSecondary: '#f9fafb',
    bgTertiary: '#f3f4f6',
    white: '#ffffff',

    // Recessed page canvas. Deliberately darker than bgSecondary so the white
    // section cards that sit on it read as raised surfaces.
    bgPage: '#eef1f5',

    // Borders
    border: '#e5e7eb',
    borderFocus: '#3b82f6',
};

// Typography
export const typography = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: {
        xs: '1.05rem',
        sm: '1.2rem',
        base: '1.3rem',
        lg: '1.4rem',
        xl: '1.5rem',
        '2xl': '1.7rem',
        '3xl': '1.875rem',
    },
    fontWeight: {
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    },
    lineHeight: {
        tight: 1.25,
        normal: 1.5,
        relaxed: 1.75,
    },
};

// Shadows
export const shadows = {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

// Component styles
export const sectionContainer: React.CSSProperties = {
    background: colors.bgPrimary,
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    boxShadow: '0 1px 2px rgba(17, 24, 39, 0.05)',
    border: `1px solid ${colors.gray300}`,
};

export const sectionHeader: React.CSSProperties = {
    margin: '0 0 0.75rem 0',
    paddingBottom: '0.65rem',
    borderBottom: `1px solid ${colors.gray200}`,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray900,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
};

// LeasePage uses these restrained accent frames to make adjacent operational
// sections easier to scan without changing the layout inside each section.
export const leaseSectionFrame: React.CSSProperties = {
    padding: '0.3rem',
    border: `1px solid ${colors.gray300}`,
    borderLeftWidth: '4px',
    borderRadius: '10px',
    backgroundColor: '#e7ebf0',
    scrollMarginTop: '0.75rem',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
};

export const leaseSectionFrameActive: React.CSSProperties = {
    boxShadow: '0 0 0 2px rgba(30, 64, 175, 0.12)',
};

export const leaseSectionAccents = {
    blue: { borderLeftColor: '#3b82f6', backgroundColor: '#d8e3f2' },
    teal: { borderLeftColor: '#0f766e', backgroundColor: '#d5e6e3' },
    amber: { borderLeftColor: '#b45309', backgroundColor: '#eadfce' },
    violet: { borderLeftColor: '#7c3aed', backgroundColor: '#e1d9ee' },
    slate: { borderLeftColor: '#64748b', backgroundColor: '#dce1e7' },
};

// Shared ordering for Lease Agreements section frames and nested invoice cards.
// Updating a color above automatically updates both levels of the page.
export const leaseAccentSequence = [
    leaseSectionAccents.blue,
    leaseSectionAccents.teal,
    leaseSectionAccents.amber,
    leaseSectionAccents.violet,
    leaseSectionAccents.slate,
];

export const fieldContainer: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
};

export const fieldLabel: React.CSSProperties = {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray500,
    marginBottom: '0',
    textTransform: 'uppercase',
    letterSpacing: '0.025em',
};

export const inputBase: React.CSSProperties = {
    width: '100%',
    padding: '0.375rem 0.5rem',
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.normal,
    color: colors.gray900,
    backgroundColor: colors.bgPrimary,
    border: `1px solid ${colors.gray300}`,
    borderRadius: '6px',
    transition: 'all 0.15s ease',
    outline: 'none',
    fontFamily: typography.fontFamily,
};

export const inputFocus = {
    borderColor: colors.borderFocus,
    boxShadow: `0 0 0 3px rgba(59, 130, 246, 0.1)`,
};

export const inputDisabled: React.CSSProperties = {
    ...inputBase,
    backgroundColor: colors.gray50,
    color: colors.gray500,
    cursor: 'not-allowed',
};

export const selectBase: React.CSSProperties = {
    ...inputBase,
    cursor: 'pointer',
    paddingRight: '1.75rem',
    // tslint:disable-next-line:max-line-length
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.375rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.25em 1.25em',
    appearance: 'none',
};

export const textareaBase: React.CSSProperties = {
    ...inputBase,
    minHeight: '80px',
    resize: 'vertical',
};

export const buttonBase: React.CSSProperties = {
    padding: '0.375rem 0.75rem',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.375rem',
    fontFamily: typography.fontFamily,
};

export const buttonPrimary: React.CSSProperties = {
    ...buttonBase,
    backgroundColor: colors.primary,
    color: '#ffffff',
};

export const buttonSecondary: React.CSSProperties = {
    ...buttonBase,
    backgroundColor: colors.gray200,
    color: colors.gray700,
};

export const buttonDanger: React.CSSProperties = {
    ...buttonBase,
    backgroundColor: colors.danger,
    color: '#ffffff',
};

export const buttonSuccess: React.CSSProperties = {
    ...buttonBase,
    backgroundColor: colors.success,
    color: '#ffffff',
};

export const cardBase: React.CSSProperties = {
    backgroundColor: colors.bgPrimary,
    borderRadius: '6px',
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    marginBottom: '0.5rem',
    transition: 'all 0.15s ease',
};

export const cardHover = {
    boxShadow: shadows.md,
    transform: 'translateY(-2px)',
};

export const emptyState: React.CSSProperties = {
    padding: '1.5rem',
    textAlign: 'center',
    color: colors.gray500,
    fontSize: typography.fontSize.sm,
};

export const tableContainer: React.CSSProperties = {
    overflowX: 'auto',
    marginTop: '1rem',
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
};

export const table: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: colors.bgPrimary,
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: shadows.sm,
};

export const tableHeader: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    backgroundColor: colors.gray50,
    borderBottom: `2px solid ${colors.border}`,
    textAlign: 'left',
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

export const tableRow: React.CSSProperties = {
    transition: 'background-color 0.15s ease',
};

export const tableCell: React.CSSProperties = {
    padding: '0.5rem 0.75rem',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: typography.fontSize.sm,
    color: colors.gray900,
};

export const badge: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.75rem',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    borderRadius: '9999px',
};

export const gridContainer: React.CSSProperties = {
    display: 'grid',
    gap: '0.75rem',
};

// Responsive grid layouts
export const gridCols2: React.CSSProperties = {
    ...gridContainer,
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
};

export const gridCols3: React.CSSProperties = {
    ...gridContainer,
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
};

// Animation classes
export const fadeIn = {
    animation: 'fadeIn 0.3s ease-in-out',
    '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
    },
};

export const slideIn = {
    animation: 'slideIn 0.3s ease-out',
    '@keyframes slideIn': {
        from: { transform: 'translateY(10px)', opacity: 0 },
        to: { transform: 'translateY(0)', opacity: 1 },
    },
};



// WEBPACK FOOTER //
// ./src/components/leases/sections/styles.ts